import React, {Component} from 'react';
import PropTypes from 'prop-types';
import cN from 'classnames';
import debounce from 'lodash/debounce';
import isInteger from 'lodash/isInteger';

// noUiSlider
import noUiSlider from 'nouislider';
import 'nouislider/distribute/nouislider.min.css';

// Space to keep free between two neighbouring scale labels, in pixels.
const LABEL_GUTTER = 6;

/**
 * A convenient range component that renders to a materialize row.
 */
class Range extends Component {

    static DISCRETE = 'discrete';
    static CONTINUOUS = 'continuous';

    static POSITION_RANDOM = 'random';
    static POSITION_CENTERED = 'centered';

    render() {
        const {showHandle} = this.props;

        return (
            <div className={cN('noUi-range', {'no-handle': !showHandle})} ref={this.ref} />
        );
    }

    /**
     * Returns how many steps to skip between two labelled steps.
     *
     * Only strides that divide the scale evenly are considered, so the labels
     * read as a regular scale (-10, -5, 0, 5, 10) instead of landing on
     * arbitrary values, and the last step always carries a label. Among the
     * candidates the smallest one wins that also labels the centre of the
     * scale - the neutral point of a bipolar scale is worth showing.
     *
     * Returns null when not even two labels fit.
     */
    static getLabelStride(last, fitting) {
        const centre = last % 2 === 0 ? last / 2 : null;

        let fallback = null;

        for (let stride = 1; stride <= last; stride++) {
            if (last % stride) continue;
            if ((last / stride) + 1 > fitting) continue;

            if (fallback === null) {
                fallback = stride;
            }

            if (centre === null || centre % stride === 0) {
                return stride;
            }
        }

        return fallback;
    }

    componentDidMount() {
        window.addEventListener('resize', this.onResize);
        window.addEventListener('orientationchange', this.onResize);

        // Covers what the window events do not: a slider that is laid out
        // after it was created, for instance because its block was still
        // hidden, and a container that changes width on its own.
        if (this.slider && typeof window.ResizeObserver === 'function') {
            this.observer = new window.ResizeObserver(this.onResize);
            this.observer.observe(this.slider);
        }
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.onResize);
        window.removeEventListener('orientationchange', this.onResize);
        this.onResize.cancel();

        if (this.observer) {
            this.observer.disconnect();
            this.observer = null;
        }
    }

    /**
     * Updates the value of the slider that was passed in new props
     */
    componentWillUpdate(nextProps) {
        const {value} = nextProps;

        this.slider.noUiSlider.set(value);
    }

    onResize = debounce(() => this.thinOutScaleLabels(), 150);

    /**
     * Hides scale labels that do not fit next to each other.
     *
     * A discrete slider draws one label per step, which collide as soon as
     * the scale has many steps or the screen is narrow - a -10 to 10 scale on
     * a phone is the worst case. The tick marks stay for every step, only
     * their labels are thinned out to an even stride, so the first, the last
     * and - whenever the stride allows - the centre of the scale keep their
     * label.
     *
     * The stride comes from measuring the rendered labels rather than from
     * assuming a font size, which keeps it correct for both languages and
     * for whatever the stylesheet does at a given breakpoint.
     */
    thinOutScaleLabels() {
        if (!this.slider) return;

        const pips = this.slider.querySelector('.noUi-pips');

        if (!pips) return;

        const labels = Array.prototype.slice.call(pips.querySelectorAll('.noUi-value'));

        if (labels.length < 3) return;

        // Measure everything unhidden, otherwise hidden labels report zero.
        labels.forEach(label => {
            label.style.display = '';
        });

        const available = pips.getBoundingClientRect().width;
        const widest = labels.reduce(
            (result, label) => Math.max(result, label.getBoundingClientRect().width),
            0
        );

        if (!available || !widest) return;

        const fitting = Math.floor(available / (widest + LABEL_GUTTER));
        const last = labels.length - 1;

        // Room for everything - nothing to thin out.
        if (fitting >= labels.length) return;

        // Both ends of the scale always keep their label, the stride fills in
        // between. Without a usable stride only the two ends are labelled.
        const stride = Range.getLabelStride(last, fitting);
        const visible = new Set([0, last]);

        if (stride) {
            for (let index = 0; index <= last; index += stride) {
                visible.add(index);
            }
        }

        labels.forEach((label, index) => {
            label.style.display = visible.has(index) ? '' : 'none';
        });
    }

    /**
     * Initializes and Configures a NoUiSlider
     * see https://refreshless.com/nouislider/ for the docs
     */
    ref = node => {
        if (!node) return;

        this.slider = node;

        const {min, max, step, value, continuity, showNumbers, onChange} = this.props;

        const isDiscrete = continuity === Range.DISCRETE;

        const options = isDiscrete ? {
            pips: {
                mode: 'steps',
                density: 100 / Math.abs(max - min) * step,
                format: showNumbers ? null :{to: () => '', from: () => ''},
            },
            format: {to: Math.round, from: Math.round},
        } : {};

        noUiSlider.create(this.slider, {
            start: isInteger(value) ? value : this.defaultValue,
            range: {
                min,
                max,
            },
            step: isDiscrete ? step : 1,
            ...options,
        }).on('change', value => {
            const event = {
                currentTarget: {value},
            };
            onChange(event);
        });

        if (isDiscrete && showNumbers) {
            this.thinOutScaleLabels();
        }
    };

    /**
     * Returns the default value to set if no value was provided
     */
    get defaultValue() {
        const {min, max, position} = this.props;

        if (position === Range.POSITION_CENTERED) return Math.floor((min + max) / 2);

        return Math.floor(Math.random() * ((max - min) + 1)) + min;
    }
}

Range.propTypes = {
    value: PropTypes.number,
    min: PropTypes.number.isRequired,
    max: PropTypes.number.isRequired,
    step: PropTypes.number,
    continuity: PropTypes.oneOf([Range.DISCRETE, Range.CONTINUOUS]),
    showNumbers: PropTypes.bool,
    showHandle: PropTypes.bool,
    onChange: PropTypes.func.isRequired,
    position: PropTypes.oneOf([Range.POSITION_CENTERED, Range.POSITION_RANDOM]),
};

Range.defaultProps = {
    step: 1,
    value: 0,
    continuity: Range.CONTINUOUS,
    position: Range.POSITION_CENTERED,
    showNumbers: true,
    showHandle: true,
};

export default Range;



// WEBPACK FOOTER //
// ./scripts/components/Range.js